import React, { useState, useEffect } from 'react';
import { Camera, Users, Eye, Heart, MessageCircle, TrendingUp, RefreshCw, UserPlus, BarChart3, Sparkles, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { analyticsAPI, instagramPostsAPI } from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';

const InstagramInsights = ({ clienteId, instanciaId, filtros, refreshTrigger }) => {
  const { darkMode } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topCommenters, setTopCommenters] = useState(null);
  const [importingComments, setImportingComments] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userComments, setUserComments] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtros?.dataInicio) params.dataInicio = filtros.dataInicio;
      if (filtros?.dataFim) params.dataFim = filtros.dataFim;
      
      const [insightsRes, commentersRes] = await Promise.all([
        analyticsAPI.getInstagramInsights(clienteId, params),
        instagramPostsAPI.getTopCommenters(clienteId).catch(() => ({ data: [] })),
      ]);
      setData(insightsRes.data);
      setTopCommenters(commentersRes.data);
    } catch (e) {
      console.error('Erro ao carregar Instagram insights:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleImportComments = async () => {
    setImportingComments(true);
    try {
      const res = await instagramPostsAPI.importAllComments(clienteId);
      const commentersRes = await instagramPostsAPI.getTopCommenters(clienteId);
      setTopCommenters(commentersRes.data);
    } catch (e) { console.error(e); }
    finally { setImportingComments(false); }
  };

  useEffect(() => { if (clienteId) load(); }, [clienteId, filtros?.dataInicio, filtros?.dataFim, refreshTrigger]);

  if (loading) return (
    <div className={`rounded-xl p-6 ${darkMode ? 'bg-zinc-800' : 'bg-white'} shadow`}>
      <div className="animate-pulse space-y-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />
          <div className={`h-5 w-48 rounded ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <div key={i} className={`h-20 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />)}
        </div>
        <div className={`h-48 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`} />
      </div>
    </div>
  );

  if (!data?.contas?.length) return null;

  const filteredContas = instanciaId
    ? data.contas.filter(c => c.instancia_id === instanciaId)
    : data.contas;

  if (!filteredContas.length) return null;

  const cardBg = darkMode ? 'bg-zinc-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const gridBg = darkMode ? '#3f3f46' : '#e5e7eb';
  const tooltipBg = darkMode ? '#27272a' : '#fff';
  const tooltipBorder = darkMode ? '#3f3f46' : '#e5e7eb';

  const renderAccount = (conta) => {
    if (conta.error) return null;
    const { profile, posts, insights, conversas } = conta;

    // Processar séries temporais dos insights
    const getInsightSeries = (name) => {
      const metric = insights.find(i => i.name === name);
      return (metric?.values || []).map(v => ({
        date: new Date(v.end_time).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        value: v.value || 0,
      }));
    };

    const getInsightTotal = (name) => {
      const metric = insights.find(i => i.name === name);
      if (!metric) return 0;
      // total_value format (new metrics)
      if (metric.total_value !== undefined) return metric.total_value.value || 0;
      // time_series format
      if (!metric.values) return 0;
      return metric.values.reduce((sum, v) => sum + (v.value || 0), 0);
    };

    const reachSeries = getInsightSeries('reach');
    const followerSeries = getInsightSeries('follower_count');

    const combinedSeries = reachSeries.map((item) => ({
      date: item.date,
      alcance: item.value,
    }));

    const reach = getInsightTotal('reach');
    const profileViews = getInsightTotal('profile_views');
    const totalInteractions = getInsightTotal('total_interactions');
    const totalLikes = getInsightTotal('likes');
    const totalComments = getInsightTotal('comments');
    const totalShares = getInsightTotal('shares');
    const totalSaves = getInsightTotal('saves');
    const followerGrowth = followerSeries.length >= 2
      ? followerSeries[followerSeries.length - 1]?.value - followerSeries[0]?.value
      : 0;

    // Totais dos posts
    const postTotalLikes = posts?.reduce((s, p) => s + p.likes, 0) || 0;
    const postTotalComments = posts?.reduce((s, p) => s + p.comments, 0) || 0;

    // Dados para pie chart de conversas
    const totalConversas = parseInt(conversas?.total_conversas) || 0;
    const respondidas = parseInt(conversas?.respondidas) || 0;
    const porHumano = parseInt(conversas?.por_humano) || 0;
    const porIA = respondidas - porHumano;
    const naoRespondidas = totalConversas - respondidas;

    const pieData = [
      { name: 'Por humano', value: porHumano, color: '#ec4899' },
      { name: 'Por IA', value: porIA, color: '#8b5cf6' },
      { name: 'Não respondidas', value: naoRespondidas, color: darkMode ? '#52525b' : '#d1d5db' },
    ].filter(d => d.value > 0);

    const statCards = [
      { label: 'Seguidores', value: profile.followers_count || 0, icon: Users, gradient: 'from-pink-500 to-rose-600', sub: followerGrowth !== 0 ? `${followerGrowth > 0 ? '+' : ''}${followerGrowth} últimos 30d` : null },
      { label: 'Alcance (30d)', value: reach, icon: Eye, gradient: 'from-violet-500 to-purple-600' },
      { label: 'Visitas ao perfil', value: profileViews, icon: TrendingUp, gradient: 'from-blue-500 to-cyan-600' },
      { label: 'Interações', value: totalInteractions, icon: Heart, gradient: 'from-orange-500 to-amber-600' },
      { label: 'Curtidas', value: totalLikes, icon: Heart, gradient: 'from-red-500 to-pink-600' },
      { label: 'Comentários', value: totalComments, icon: MessageCircle, gradient: 'from-green-500 to-emerald-600' },
    ];

    return (
      <div key={conta.instancia_id} className="space-y-4">
        {/* Header */}
        <div className={`rounded-xl p-5 ${cardBg} shadow`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {profile.profile_picture_url ? (
                <img src={profile.profile_picture_url} alt="" className="w-12 h-12 rounded-full ring-2 ring-pink-500/30" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                  <Camera className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <h3 className={`text-lg font-bold ${textPrimary}`}>📷 Instagram — @{profile.username}</h3>
                <p className={`text-sm ${textSecondary}`}>{profile.name} · {profile.media_count || 0} publicações · {profile.follows_count || 0} seguindo</p>
              </div>
            </div>
            <button onClick={() => setCollapsed(prev => ({ ...prev, [conta.instancia_id]: !prev[conta.instancia_id] }))} className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
              {collapsed[conta.instancia_id] ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {!collapsed[conta.instancia_id] && (
          <>
        {/* Stat Cards */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3">
          {statCards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div key={i} className={`rounded-xl p-2 sm:p-3 ${cardBg} shadow`}>
                <div className="flex items-center gap-1 mb-1 sm:mb-1.5">
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md bg-gradient-to-br ${card.gradient} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-3 h-3 text-white" />
                  </div>
                  <span className={`text-[10px] sm:text-xs font-medium truncate ${textSecondary}`}>{card.label}</span>
                </div>
                <p className={`text-sm sm:text-lg font-bold ${textPrimary}`}>{card.value.toLocaleString('pt-BR')}</p>
                {card.sub && <p className={`text-xs ${followerGrowth > 0 ? 'text-green-500' : 'text-red-500'}`}>{card.sub}</p>}
              </div>
            );
          })}
        </div>

        {/* Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Alcance e Engajamento */}
          <div className={`rounded-xl p-5 ${cardBg} shadow lg:col-span-2`}>
            <h4 className={`text-sm font-semibold mb-4 ${textPrimary}`}>
              <Eye className="w-4 h-4 inline mr-1" /> Alcance (30 dias)
            </h4>
            {combinedSeries.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={combinedSeries}>
                  <defs>
                    <linearGradient id="gradAlcance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridBg} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }} />
                  <Tooltip contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 8, fontSize: 12 }} />
                  <Area type="monotone" dataKey="alcance" stroke="#ec4899" fill="url(#gradAlcance)" strokeWidth={2} name="Alcance" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p className={`text-sm text-center py-10 ${textSecondary}`}>Sem dados de insights disponíveis</p>
            )}
          </div>

          {/* Conversas Pie */}
          <div className={`rounded-xl p-5 ${cardBg} shadow`}>
            <h4 className={`text-sm font-semibold mb-4 ${textPrimary}`}>
              <MessageCircle className="w-4 h-4 inline mr-1" /> Conversas
            </h4>
            {totalConversas > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {pieData.map((d, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                        <span className={textSecondary}>{d.name}</span>
                      </div>
                      <span className={`font-medium ${textPrimary}`}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className={`text-sm text-center py-10 ${textSecondary}`}>Nenhuma conversa ainda</p>
            )}
            <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-zinc-700' : 'border-gray-100'}`}>
              <p className={`text-xs ${textSecondary}`}>Últimas 24h: <span className={`font-semibold ${textPrimary}`}>{conversas?.ultimas_24h || 0}</span> conversas</p>
            </div>
          </div>
        </div>

        {/* Seguidores ao longo do tempo */}
        {followerSeries.length > 2 && (
          <div className={`rounded-xl p-5 ${cardBg} shadow`}>
            <h4 className={`text-sm font-semibold mb-4 ${textPrimary}`}>
              <UserPlus className="w-4 h-4 inline mr-1" /> Evolução de Seguidores (30 dias)
            </h4>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={followerSeries}>
                <defs>
                  <linearGradient id="gradFollowers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ec4899" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={gridBg} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }} domain={[0, 'auto']} />
                <Tooltip contentStyle={{ backgroundColor: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="value" stroke="#ec4899" fill="url(#gradFollowers)" strokeWidth={2} name="Seguidores" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Posts Recentes */}
        {posts?.length > 0 && (
          <div className={`rounded-xl p-5 ${cardBg} shadow`}>
            <h4 className={`text-sm font-semibold mb-4 ${textPrimary}`}>
              📝 Posts Recentes ({posts.length})
              <span className={`ml-3 text-xs font-normal ${textSecondary}`}>
                Total: {postTotalLikes} curtidas · {postTotalComments} comentários
              </span>
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {posts.slice(0, 9).map(post => (
                <div key={post.id} className={`rounded-lg border p-3 ${darkMode ? 'border-zinc-700 bg-zinc-900/50' : 'border-gray-100 bg-gray-50'}`}>
                  {post.media_url && (
                    <img src={post.media_url} alt="" className="w-full h-32 object-cover rounded-md mb-2" />
                  )}
                  <p className={`text-xs line-clamp-2 mb-2 ${textPrimary}`}>{post.caption || '(sem legenda)'}</p>
                  <div className="flex items-center gap-3 text-xs">
                    <span className={textSecondary}>❤️ {post.likes}</span>
                    <span className={textSecondary}>💬 {post.comments}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${darkMode ? 'bg-zinc-700' : 'bg-gray-200'}`}>{post.media_type}</span>
                  </div>
                  <p className={`text-xs mt-1 ${textSecondary}`}>{new Date(post.timestamp).toLocaleDateString('pt-BR')}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </div>
    );
  };

  // Top Commenters Section
  const openUserComments = async (username) => {
    setSelectedUser(username);
    setLoadingUser(true);
    try {
      const res = await instagramPostsAPI.getUserComments(clienteId, username);
      setUserComments(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingUser(false); }
  };

  const CommentersSection = () => {
    const cardBg = darkMode ? 'bg-zinc-800' : 'bg-white';
    const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
    const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';

    const sentimentEmoji = { positivo: '😊', neutro: '😐', negativo: '😞' };

    return (
      <>
      <div className={`rounded-xl p-6 ${cardBg} shadow mt-6`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-pink-500" />
            <h3 className={`font-semibold ${textPrimary}`}>Top Comentadores</h3>
          </div>
          <button onClick={handleImportComments} disabled={importingComments}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            {importingComments ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Sincronizar Comentários
          </button>
        </div>
        {!topCommenters?.length ? (
          <p className={`text-sm ${textSecondary}`}>Nenhum comentário importado ainda. Clique em "Sincronizar Comentários".</p>
        ) : (
          <div className="space-y-2">
            {topCommenters.slice(0, 15).map((c, i) => (
              <div key={c.username} onClick={() => openUserComments(c.username)}
                className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition ${darkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-gray-50'}`}>
                <span className={`w-6 text-center text-sm font-bold ${i < 3 ? 'text-pink-500' : textSecondary}`}>{i + 1}</span>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                  {c.username?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${textPrimary}`}>@{c.username}</p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className={`font-semibold ${textPrimary}`}>{c.total} 💬</span>
                  {Number(c.positivos) > 0 && <span className="text-green-500">{c.positivos} 😊</span>}
                  {Number(c.negativos) > 0 && <span className="text-red-500">{c.negativos} 😞</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de comentários do usuário */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedUser(null)}>
          <div className={`w-full max-w-2xl rounded-2xl ${cardBg} shadow-2xl max-h-[80vh] flex flex-col`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-5 border-b ${darkMode ? 'border-zinc-700' : 'border-gray-200'}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold">
                  {selectedUser.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className={`font-semibold ${textPrimary}`}>@{selectedUser}</p>
                  <p className={`text-xs ${textSecondary}`}>{userComments.length} comentário(s)</p>
                </div>
              </div>
              <button onClick={() => setSelectedUser(null)} className={`p-2 rounded-lg transition ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}>
                <span className="text-lg">✕</span>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {loadingUser ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-pink-500" /></div>
              ) : userComments.map((c, i) => (
                <div key={i} className={`p-4 rounded-xl border ${darkMode ? 'border-zinc-700 bg-zinc-900/50' : 'border-gray-100 bg-gray-50'}`}>
                  <div className="flex items-start gap-3">
                    {c.cover_url && (
                      <img src={c.cover_url} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>{c.tipo}</span>
                        {c.sentimento && <span className="text-xs">{sentimentEmoji[c.sentimento] || ''}</span>}
                        <span className={`text-xs ${textSecondary}`}>{new Date(c.commented_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                      </div>
                      <p className={`text-sm mt-1 ${textPrimary}`}>"{c.text}"</p>
                      {c.caption && <p className={`text-xs mt-1.5 truncate ${textSecondary}`}>📝 {c.caption.slice(0, 80)}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>
    );
  };

  return (
    <>
      {filteredContas.map(renderAccount)}
      <CommentersSection />
    </>
  );
};

export default InstagramInsights;
